import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Building2, User, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import OTPVerification from "../../components/auth/OTPVerification";
import { systemSettingsAPI } from "../../services/api.js";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [skipOtpForDemo, setSkipOtpForDemo] = useState(false);
  const [platformName, setPlatformName] = useState(
    "Tokenised Bond Pilot Project"
  );
  const { login, isLoggingIn, otpSession, clearOtpSession } = useAuth();

  // Load platform name from system settings
  useEffect(() => {
    const loadPlatformName = async () => {
      try {
        const response = await systemSettingsAPI.getPublicSettings();
        if (response.data.success) {
          setPlatformName(response.data.data.platformName);
        }
      } catch (error) {
        console.error("Failed to load platform name:", error);
        // Keep default name if API fails
      }
    };

    loadPlatformName();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await login({
        email: data.email,
        password: data.password,
        skipOtpForDemo: skipOtpForDemo,
      });
    } catch (error) {
      // Error handling is done in the auth context
    }
  };

  const handleBackToLogin = () => {
    clearOtpSession();
  };

  // Demo accounts data
  const demoAccounts = [
    { role: "Issuer", email: "issuer@demo.com", password: "password" },
    { role: "Facility Agent", email: "fa@demo.com", password: "password" },
    {
      role: "Primary Subscriber",
      email: "primarysubscriber@demo.com",
      password: "password",
    },
    {
      role: "Primary Subscriber",
      email: "psdemo@demo.com",
      password: "Pa$$w0rd",
    },
    { role: "Custodian", email: "custodian@demo.com", password: "password" },
    { role: "Custodian", email: "custodian+1@demo.com", password: "Pa$$w0rd" },
    { role: "Super Admin", email: "admin@demo.com", password: "password" },
    { role: "Investor", email: "investor@demo.com", password: "password" },
    { role: "Trustee", email: "trustee@demo.com", password: "password" },
    {
      role: "Sub Investor",
      email: "sub_investor@demo.com",
      password: "password",
    },
  ];

  // Auto-fill login form with demo credentials
  const fillDemoCredentials = (email, password) => {
    setValue("email", email);
    setValue("password", password);
  };

  // Show OTP verification if OTP session exists
  if (otpSession) {
    return <OTPVerification onBack={handleBackToLogin} />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side - Branding */}
      <div className="relative hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h1 className="text-4xl font-bold text-white">
                {platformName}
              </h1>            
                <span className="px-3 py-1 text-sm font-semibold tracking-wider text-black uppercase bg-yellow-400 rounded-full">
                  SIT
                </span>
            </div>
            <p className="text-xl text-blue-100">
              Revolutionizing bond issuance and management through blockchain
              technology
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-white">
                  Multi-Stakeholder Platform
                </h3>
                <p className="text-blue-100">
                  Seamless collaboration between issuers, arrangers, and
                  investors
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-white">
                  Comprehensive Compliance
                </h3>
                <p className="text-blue-100">
                  Built-in regulatory compliance and audit trails for
                  transparency
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex items-center justify-center w-full px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* Demo Mode Banner */}
          {skipOtpForDemo && (
            <div className="p-4 mb-6 bg-yellow-100 border-l-4 border-yellow-500 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Developer Mode Active:</strong> All accounts will
                    bypass OTP verification
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 text-center">
            <div className="flex justify-center mb-6">
              <img
                src={`${process.env.PUBLIC_URL}/khazanal_logo-name.svg`}
                alt="Khazanah Logo"
                className="w-auto h-16"
              />
            </div>
            <h2 className="mb-2 text-3xl font-bold text-secondary-900">
              Welcome Back
            </h2>
            <p className="text-secondary-600">Sign in to your TBPP account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className={`form-input ${
                  errors.email ? "form-input-error" : ""
                }`}
                placeholder="Enter your email"
                {...register("email", {
                  required: "Email address is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="mb-0 form-label">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-500 hover:text-primary-600"
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-input pr-10 ${
                    errors.password ? "form-input-error" : ""
                  }`}
                  placeholder="Enter your password"
                  {...register("password", {
                    required: "Password is required",
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-secondary-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-secondary-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="form-error">{errors.password.message}</p>
              )}
            </div>

            {/* Skip OTP Checkbox */}
            <div className="flex items-center">
              <input
                id="skipOtpDemo"
                type="checkbox"
                checked={skipOtpForDemo}
                onChange={(e) => setSkipOtpForDemo(e.target.checked)}
                className="w-4 h-4 border-gray-300 rounded text-primary-600 focus:ring-primary-500"
              />
              <label
                htmlFor="skipOtpDemo"
                className="block ml-2 text-sm text-gray-700"
              >
                Skip OTP verification
              </label>
            </div>

            {skipOtpForDemo && (
              <div className="p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Developer Mode:</strong> This bypasses OTP
                      verification for all accounts. Only use for testing
                      purposes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoggingIn}
              className={`w-full btn-lg flex items-center justify-center ${
                skipOtpForDemo
                  ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                  : "btn-primary"
              }`}
            >
              {isLoggingIn ? (
                <LoadingSpinner size="sm" text="" />
              ) : skipOtpForDemo ? (
                "Sign In (Skip OTP)"
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-secondary-600">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="font-medium text-primary-500 hover:text-primary-600"
              >
                Register Now
              </Link>
            </p>
          </div>         
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
